/**
 * Header notifications bell (R5 / ADR-0031).
 *
 * Polls `notifications.list_due` every 60 seconds while the tab is
 * foregrounded. The badge shows the total count of currently-due,
 * unacknowledged reminders for the user's visible group set; the
 * dropdown lists the top N most-recently-due. Clicking a row navigates
 * to the contract's Notifications tab via `?tab=paminnelser`.
 *
 * This is the "don't miss it" surface that replaces email in the MVP
 * scope. Future email phase reuses the same data; see ADR-0031.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  tenantApi,
  type DueNotificationRow,
  type NotificationKind,
} from '../api.js';
import {
  groupReminders,
  reminderRowLabel,
  type ReminderGroup,
} from '../lib/reminder-groups.js';
import { useTenantQuery } from '../query.js';

const KIND_LABELS: Record<NotificationKind, string> = {
  termination_notice: 'Uppsägning',
  negotiation_window: 'Förhandlingsfönster',
  lease_end: 'Hyresavtal löper ut',
  agreement_end: 'Förhandlingsordning löper ut',
  signature_overdue: 'Inväntar signering',
  custom: 'Påminnelse',
};

function groupTitle(
  g: ReminderGroup<DueNotificationRow>,
  freshest: DueNotificationRow,
): string {
  // Shared per-occurrence label wins (custom rows). When occurrences
  // carry their own labels (in-flight escalation steps), surface the
  // freshest one — that step's wording is the actionable signal.
  return g.sharedLabel ?? reminderRowLabel(freshest) ?? KIND_LABELS[g.kind];
}

function relativeDays(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const diff = Math.round((d.getTime() - now.getTime()) / 86_400_000);
  if (diff === 0) return 'idag';
  if (diff > 0) return `om ${diff} dagar`;
  if (diff === -1) return 'igår';
  return `${Math.abs(diff)} dagar sedan`;
}

export function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Poll every 60s while the tab is foreground. SWR pauses revalidation
  // on hidden tabs by default, so this is effectively idle when the user
  // is elsewhere.
  // Fetch the server max (50): grouping runs client-side, and a
  // truncated page would split groups / undercount "· N påminnelser".
  // Display still caps at 10 grouped entries.
  const q = useTenantQuery(
    'notifications.list_due',
    () => tenantApi.notifications.list_due({ limit: 50 }),
    [],
    {
      ignoreActiveGroup: true,
      refreshInterval: 60_000,
      revalidateOnFocus: true,
    },
  );

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const total = q.data?.total ?? 0;
  const rows = q.data?.notifications ?? [];
  // One entry per grouped reminder (REGTA-51): a lifecycle event whose
  // 3mo + 1mo leads are both due shows once, with a count.
  const groups = useMemo(() => groupReminders(rows).slice(0, 10), [rows]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-8 w-8 items-center justify-center rounded-full text-stone-600 hover:bg-stone-100 hover:text-stone-900"
        aria-label="Påminnelser"
      >
        <BellIcon />
        {total > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-medium text-white">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 w-96 overflow-hidden rounded-md border border-stone-200 bg-white shadow-lg">
          <header className="flex items-center justify-between border-b border-stone-200 px-4 py-2">
            <h3 className="text-xs font-medium uppercase tracking-wider text-stone-500">
              Påminnelser
            </h3>
            {total > 0 && (
              <span className="text-[10px] text-stone-400">{total} aktiva</span>
            )}
          </header>
          {q.isLoading && (
            <div className="p-6 text-center text-xs text-stone-400">Laddar…</div>
          )}
          {!q.isLoading && rows.length === 0 && (
            <div className="p-6 text-center text-xs text-stone-500">
              Inga påminnelser just nu.
            </div>
          )}
          {groups.length > 0 && (
            <ul className="max-h-[60vh] divide-y divide-stone-100 overflow-y-auto">
              {groups.map((g) => {
                const freshest = g.current ?? g.rows[g.rows.length - 1]!;
                return (
                  <li key={g.key}>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        navigate(
                          `/contracts/${freshest.contractId}/edit?tab=paminnelser`,
                        );
                      }}
                      className="flex w-full flex-col items-start gap-0.5 px-4 py-2.5 text-left hover:bg-stone-50"
                    >
                      <div className="flex w-full items-center justify-between gap-2">
                        <span className="text-xs font-medium text-stone-900">
                          {groupTitle(g, freshest)}
                          {g.rows.length > 1 && (
                            <span className="ml-1.5 font-normal text-stone-400">
                              · {g.rows.length} påminnelser
                            </span>
                          )}
                        </span>
                        <span className="text-[10px] text-stone-400">
                          {relativeDays(freshest.fireAt)}
                        </span>
                      </div>
                      <span className="text-[11px] text-stone-500 truncate w-full">
                        {freshest.contractTitle}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM8.5 16a1.5 1.5 0 003 0h-3z"
        clipRule="evenodd"
      />
    </svg>
  );
}
